import { currentContext, flushInterval, getToken, isDebug, parseToken, safe, setToken, state } from './config'
import {
  allowEvent,
  isActive,
  journeyId,
  nextSeq,
  pageSeq,
  setPageSeq,
  storageGet,
  storageRemove,
  storageSet,
} from './journey'
import { clampInt } from './scrub'

const MAX_BUFFER = 500
const MAX_BEFORE_CONFIG = 50
const FLUSH_EVENTS = 40
const FLUSH_BYTES = 48 * 1024
const MAX_BATCH_EVENTS = 100
const MAX_BODY_BYTES = 60 * 1024
const ENVELOPE_BYTES = 1024
const HIGH_PRIORITY_MS = 2000
const RETRY_MS = 5000
const MAX_FAILURES = 3
const PAUSE_MS = 60000
const MAX_PAUSE_MS = 15 * 60000
const DEFAULT_RETRY_AFTER_MS = 60000
const MAX_ATTEMPTS = 10
const MAX_QUEUED_EVENTS = 1000
const MAX_HELD_BATCHES = 20
const OUTBOX_KEY = 'helios.outbox'
const OUTBOX_BYTES = 100 * 1024
const PAGE_CAPS = { 'ui.click': 200, 'error.js': 20, 'outcome.toast': 50 }

const MASK_TARGET_TYPES = new Set([
  'ui.click',
  'ui.rage_click',
  'ui.key',
  'form.engage',
  'form.submit',
  'form.invalid',
  'form.abandon',
])
const MASK_PROP_TYPES = new Set([...MASK_TARGET_TYPES, 'nav.request'])
const MASKED_PROPS = ['submit_label', 'trigger_label', 'dialog', 'column', 'field', 'last_field', 'fields_touched']

const ID_PATTERN = /^[A-Za-z0-9_:.-]{1,128}$/
const ROUTE_PATTERN = /^[A-Za-z0-9._-]{1,128}$/
const COMPONENT_PATTERN = /^[A-Za-z0-9/_.-]{1,128}$/

const encoder = typeof TextEncoder === 'function' ? new TextEncoder() : null
const pending = new WeakSet()
const outstanding = new Set()

let buffer = []
let bufferBytes = 0
let queue = []
let held = []
let expiredToken = null
let beforeConfig = []
let pageCounts = { journey: null, page: null, counts: {} }
let dropped = 0
let endpoint = null
let timer = null
let timerDue = 0
let inFlight = false
let holdUntil = 0
let nextSendAt = 0
let pauseMs = 0
let failures = 0

function byteLength(text) {
  return encoder ? encoder.encode(text).length : text.length * 3
}

function validId(value) {
  return typeof value === 'string' && ID_PATTERN.test(value) ? value : null
}

function matching(pattern, value) {
  return typeof value === 'string' && pattern.test(value) ? value : null
}

function compact(props) {
  const result = {}

  for (const [key, value] of Object.entries(props ?? {})) {
    if (value === undefined || value === null || value === '') {
      continue
    }

    if (Array.isArray(value) ? value.length === 0 : typeof value === 'object' && Object.keys(value).length === 0) {
      continue
    }

    result[key] = value
  }

  return result
}

function clearTimer() {
  if (timer !== null) {
    window.clearTimeout(timer)
    timer = null
  }
}

function schedule(delay) {
  const wait = Math.max(delay, 0)
  const due = Date.now() + wait

  if (timer !== null && timerDue <= due) {
    return
  }

  clearTimer()
  timerDue = due
  timer = window.setTimeout(
    safe(() => {
      timer = null
      flush()
    }),
    wait
  )
}

export function snapshot() {
  return { ...currentContext(), page_seq: pageSeq() }
}

// Reserves a seq now for an event emitted later (held clicks, visits, flash checks), so replay order follows the action.
export function reserve(snap = snapshot()) {
  if (!isActive()) {
    return null
  }

  const slot = {
    journey: journeyId(),
    token: getToken()?.value ?? null,
    seq: nextSeq(),
    page_seq: snap.page_seq,
    ts: Date.now(),
    ctx: snap,
  }

  outstanding.add(slot)

  return slot
}

export function release(slot) {
  if (slot) {
    outstanding.delete(slot)
  }
}

function withinPageCap(type, page) {
  const cap = PAGE_CAPS[type]

  if (!cap) {
    return true
  }

  if (pageCounts.journey !== journeyId() || pageCounts.page !== page) {
    pageCounts = { journey: journeyId(), page, counts: {} }
  }

  const count = (pageCounts.counts[type] ?? 0) + 1

  if (count > cap) {
    return false
  }

  pageCounts.counts[type] = count

  return true
}

function buildEvent(type, fields, slot) {
  const ctx = slot.ctx
  const masked = ctx.masked === true
  const props = compact(fields.props)

  if (masked && MASK_PROP_TYPES.has(type)) {
    MASKED_PROPS.forEach((key) => delete props[key])
  }

  const event = {
    seq: slot.seq,
    page_seq: slot.page_seq,
    ts: Math.round(fields.ts ?? slot.ts),
    type,
    correlation_id: validId(fields.correlation_id !== undefined ? fields.correlation_id : ctx.correlation_id),
    route_name: matching(ROUTE_PATTERN, ctx.route_name),
    component: matching(COMPONENT_PATTERN, ctx.component),
    path: typeof ctx.path === 'string' ? ctx.path : null,
    target: masked && MASK_TARGET_TYPES.has(type) ? null : typeof fields.target === 'string' ? fields.target : null,
    status: clampInt(fields.status, 100, 599),
    duration_ms: clampInt(fields.duration_ms, 0, 3600000),
  }

  Object.keys(event).forEach((key) => {
    if ((event[key] === null || event[key] === undefined) && key !== 'page_seq') {
      delete event[key]
    }
  })

  if (event.target === undefined) {
    delete props.label_src
  }

  if (Object.keys(props).length > 0) {
    event.props = props
  }

  return event
}

function isHighPriority(event) {
  if (event.type.startsWith('error.')) {
    return true
  }

  if (event.type === 'nav.request' || event.type === 'xhr.request') {
    return event.props?.outcome !== 'success'
  }

  return event.type === 'outcome.toast' && event.target === 'error'
}

function evict() {
  let index = buffer.findIndex((entry) => entry.event.type === 'ui.click')

  if (index === -1) {
    index = buffer.findIndex((entry) => !entry.event.type.startsWith('error.'))
  }

  const [entry] = buffer.splice(index === -1 ? 0 : index, 1)

  bufferBytes -= entry.bytes
  pending.delete(entry.event)
  dropped += 1
}

function push(entry) {
  if (buffer.length >= MAX_BUFFER) {
    evict()
  }

  entry.bytes = JSON.stringify(entry.event).length + 1
  buffer.push(entry)
  bufferBytes += entry.bytes
  pending.add(entry.event)

  if (isDebug()) {
    console.debug('[helios]', entry.event.type, entry.event)
  }

  if (buffer.length >= FLUSH_EVENTS || bufferBytes >= FLUSH_BYTES) {
    flush()
  } else {
    schedule(isHighPriority(entry.event) ? HIGH_PRIORITY_MS : flushInterval())
  }
}

export function record(type, fields = {}, slot = null) {
  if (!state.booted) {
    if (beforeConfig.length < MAX_BEFORE_CONFIG) {
      beforeConfig.push({ type, fields, ts: Date.now() })
    }

    return null
  }

  release(slot)

  if (!isActive()) {
    return null
  }

  const snap = slot?.ctx ?? snapshot()

  if (!withinPageCap(type, slot?.page_seq ?? snap.page_seq) || !allowEvent(type.startsWith('error.'))) {
    dropped += 1

    return null
  }

  const resolved = slot ?? reserve(snap)

  release(resolved)

  const event = buildEvent(type, fields, resolved)

  push({ journey: resolved.journey, token: resolved.token, event })

  return event
}

// Events raised before the first navigate (Vue errors during mount) wait for the first config, and are dropped if it is null.
export function releaseBeforeConfig() {
  const events = beforeConfig

  beforeConfig = []

  if (isActive()) {
    events.forEach(({ type, fields, ts }) => record(type, { ...fields, ts }))
  }
}

export function isPending(event) {
  return pending.has(event)
}

// Helios lives on another origin: only an absolute https URL (http while Qualify itself runs on http) is accepted.
export function useEndpoint(value) {
  try {
    const url = new URL(value)
    const scheme = url.protocol === 'https:' || (url.protocol === 'http:' && window.location.protocol === 'http:')

    if (scheme && url.search === '' && url.hash === '') {
      endpoint = url.origin + url.pathname
    }
  } catch {
    return
  }
}

function sendable() {
  return endpoint !== null && Date.now() >= nextSendAt
}

export function holdFlush(ms) {
  holdUntil = Date.now() + ms
}

function toBatches(entries) {
  const batches = []
  let batch = null
  let bytes = 0

  for (const entry of entries) {
    pending.delete(entry.event)

    if (!entry.token || !entry.journey) {
      continue
    }

    if (
      !batch ||
      batch.journey !== entry.journey ||
      batch.token !== entry.token ||
      batch.events.length >= MAX_BATCH_EVENTS ||
      bytes + entry.bytes > MAX_BODY_BYTES - ENVELOPE_BYTES
    ) {
      batch = { journey: entry.journey, token: entry.token, events: [], dropped: 0, retried: false }
      batches.push(batch)
      bytes = 0
    }

    batch.events.push(entry.event)
    bytes += entry.bytes
  }

  if (batches.length > 0) {
    batches[0].dropped = dropped
    dropped = 0
  }

  return batches
}

function prepare(batch) {
  const body = JSON.stringify({
    v: 1,
    token: batch.token,
    journey: batch.journey,
    sent_at: Date.now(),
    dropped: batch.dropped,
    events: batch.events,
  })

  if (byteLength(body) <= MAX_BODY_BYTES) {
    return [[batch, body]]
  }

  if (batch.events.length < 2) {
    return []
  }

  const middle = Math.ceil(batch.events.length / 2)

  return [
    ...prepare({ ...batch, events: batch.events.slice(0, middle) }),
    ...prepare({ ...batch, events: batch.events.slice(middle), dropped: 0 }),
  ]
}

function urlFor(batch) {
  return `${endpoint}?j=${encodeURIComponent(batch.journey)}`
}

function waitFor(delay) {
  nextSendAt = Math.max(nextSendAt, Date.now() + delay)
}

function dropBatch(batch) {
  dropped += batch.events.length + (batch.dropped ?? 0)
}

function requeue(batch, delay, attempt) {
  const attempts = (batch.attempts ?? 0) + (attempt ? 1 : 0)

  if (attempts >= MAX_ATTEMPTS) {
    dropBatch(batch)
  } else {
    queue.unshift({ ...batch, attempts })
  }

  waitFor(delay)
}

// Under a long outage the oldest queued batches go first; their events count as dropped.
function trimQueue() {
  let total = queue.reduce((sum, batch) => sum + batch.events.length, 0)

  while (total > MAX_QUEUED_EVENTS && queue.length > 1) {
    const oldest = queue.shift()

    total -= oldest.events.length
    dropBatch(oldest)
  }
}

function retryAfterMs(value) {
  if (typeof value !== 'string' || value.trim() === '') {
    return null
  }

  const seconds = Number(value)
  const ms = Number.isFinite(seconds) ? seconds * 1000 : Date.parse(value) - Date.now()

  return Number.isFinite(ms) ? Math.min(Math.max(ms, 0), MAX_PAUSE_MS) : null
}

export function disableForLoad() {
  state.disabled = true
  buffer = []
  bufferBytes = 0
  queue = []
  held = []
  clearTimer()
}

function handleResponse(batch, status, header, retryAfter) {
  if (status >= 200 && status < 300) {
    failures = 0
    pauseMs = 0

    if (header === 'off') {
      disableForLoad()
    } else if (header === 'expired') {
      expiredToken = batch.token
      hold(batch)
    }

    return
  }

  if (status === 413) {
    if (!batch.retried && batch.events.length > 1) {
      const middle = Math.ceil(batch.events.length / 2)

      queue.unshift(
        { ...batch, events: batch.events.slice(0, middle), retried: true },
        { ...batch, events: batch.events.slice(middle), dropped: 0, retried: true }
      )
    }

    return
  }

  const wait = retryAfterMs(retryAfter)

  // Budgets and a busy Helios ask to wait; that is not a failure.
  if (status === 429 || (status >= 500 && wait !== null)) {
    requeue(batch, wait ?? DEFAULT_RETRY_AFTER_MS, false)

    return
  }

  if (status === 0 || status === 403 || status === 408 || status >= 500) {
    handleFailure(batch)
  }
}

// Network errors, CORS-less or opaque answers, 403 and 5xx: retry soon, then pause from 1 minute doubling to 15.
function handleFailure(batch) {
  failures += 1

  if (failures >= MAX_FAILURES) {
    pauseMs = pauseMs > 0 ? Math.min(pauseMs * 2, MAX_PAUSE_MS) : PAUSE_MS
  }

  requeue(batch, failures >= MAX_FAILURES ? pauseMs : RETRY_MS, true)
}

function hold(batch) {
  held.push(batch)
  held = held.slice(-MAX_HELD_BATCHES)
}

function pump() {
  // An expired token waits for a newer one instead of being sent again.
  while (expiredToken !== null && queue.length > 0 && queue[0].token === expiredToken) {
    hold(queue.shift())
  }

  if (inFlight || state.disabled || endpoint === null) {
    return
  }

  const wait = Math.max(holdUntil, nextSendAt) - Date.now()

  // After a pause the first batch out is the probe; the rest follow once it succeeds.
  if (wait > 0) {
    if (queue.length > 0 || buffer.length > 0) {
      schedule(wait)
    }

    return
  }

  if (queue.length === 0) {
    return
  }

  const [pair, ...rest] = prepare(queue.shift())

  queue.unshift(...rest.map(([batch]) => batch))

  if (!pair) {
    pump()

    return
  }

  const [batch, body] = pair

  inFlight = true

  const done = safe(() => {
    inFlight = false
    pump()
  })

  try {
    window
      .fetch(urlFor(batch), {
        method: 'POST',
        keepalive: true,
        credentials: 'omit',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body,
      })
      .then(
        safe((response) =>
          handleResponse(batch, response.status, response.headers.get('X-Helios'), response.headers.get('Retry-After'))
        )
      )
      .catch(safe(() => handleFailure(batch)))
      .finally(done)
  } catch {
    handleFailure(batch)
    done()
  }
}

function readOutbox() {
  try {
    const batches = JSON.parse(storageGet(OUTBOX_KEY) ?? '[]')

    return Array.isArray(batches)
      ? batches.filter((batch) => batch?.journey && batch?.token && Array.isArray(batch?.events))
      : []
  } catch {
    return []
  }
}

function stored(batch, isHeld) {
  const { journey, token, events, dropped: count, attempts } = batch

  return { journey, token, events, dropped: count, attempts, held: isHeld }
}

// Newest kept: the oldest stored batches go first when the outbox is over its cap.
function appendOutbox(batches, heldBatches = []) {
  const all = readOutbox().concat(
    heldBatches.map((batch) => stored(batch, true)),
    batches.map((batch) => stored(batch, false))
  )

  while (all.length > 0 && byteLength(JSON.stringify(all)) > OUTBOX_BYTES) {
    all.shift()
  }

  if (all.length > 0) {
    storageSet(OUTBOX_KEY, JSON.stringify(all))
  }
}

// Held batches go out under the current token when it is newer and for the same user; otherwise they keep waiting.
function retoken(batches, next) {
  const ready = []
  const waiting = []

  batches.forEach((batch) => {
    const claims = parseToken(batch.token)

    if (next && claims && claims.user === next.user && claims.exp < next.exp) {
      ready.push({ ...batch, token: next.value, retried: false })
    } else {
      waiting.push(batch)
    }
  })

  return [ready, waiting]
}

export function restoreOutbox() {
  const batches = readOutbox().map(({ journey, token, events, dropped: count, attempts, held: isHeld }) => ({
    journey,
    token,
    events,
    dropped: Number.isInteger(count) ? count : 0,
    attempts: Number.isInteger(attempts) ? attempts : 0,
    retried: false,
    held: isHeld === true,
  }))

  storageRemove(OUTBOX_KEY)

  const [ready, waiting] = retoken(
    batches.filter((batch) => batch.held),
    getToken()
  )

  waiting.forEach(hold)
  queue.unshift(...ready, ...batches.filter((batch) => !batch.held))

  if (queue.length > 0) {
    schedule(HIGH_PRIORITY_MS)
  }
}

function sendBeacon(url, body) {
  try {
    return navigator.sendBeacon?.(url, new Blob([body], { type: 'text/plain;charset=UTF-8' })) === true
  } catch {
    return false
  }
}

// What cannot be beaconed now (no endpoint, a pause, a refused beacon) and the held batches wait in the outbox.
function beaconAll() {
  const kept = []
  const batches = queue
  const waiting = held
  const send = sendable()

  queue = []
  held = []

  for (const batch of batches) {
    for (const [part, body] of prepare(batch)) {
      if (!send || !sendBeacon(urlFor(part), body)) {
        kept.push(part)
      }
    }
  }

  if (kept.length > 0 || waiting.length > 0) {
    appendOutbox(kept, waiting)
  }
}

export function flush({ beacon = false } = {}) {
  clearTimer()

  if (state.disabled) {
    return
  }

  // Without an endpoint, or while sending waits, events stay in the buffer under its caps.
  if (buffer.length > 0 && (beacon || sendable())) {
    queue.push(...toBatches(buffer))
    buffer = []
    bufferBytes = 0
    trimQueue()
  }

  if (beacon) {
    beaconAll()
  } else {
    pump()
  }
}

// Old events keep their own token; held "expired" batches go out again only under a newer token for the same user.
export function adoptToken(next) {
  setToken(next)

  if (next.value === expiredToken) {
    return
  }

  const [ready] = retoken(held, next)

  held = []
  expiredToken = null
  queue.push(...ready)

  if (queue.length > 0) {
    pump()
  }
}

export function discardJourney(id) {
  buffer = buffer.filter((entry) => {
    if (entry.journey !== id) {
      return true
    }

    bufferBytes -= entry.bytes
    pending.delete(entry.event)

    return false
  })
}

// A duplicated tab shares the stored journey: move what this load buffered or reserved onto its new journey.
export function rekeyJourney(fromId, toId, fromPageSeq) {
  const items = [
    ...buffer.filter((entry) => entry.journey === fromId).map((entry) => ({ holder: entry, target: entry.event })),
    ...[...outstanding].filter((slot) => slot.journey === fromId).map((slot) => ({ holder: slot, target: slot })),
  ].sort((a, b) => a.target.seq - b.target.seq)

  const pages = new Map()

  for (const { holder, target } of items) {
    if (!pages.has(target.page_seq)) {
      pages.set(target.page_seq, pages.size + 1)
    }

    target.page_seq = pages.get(target.page_seq)
    target.seq = nextSeq()
    holder.journey = toId
  }

  setPageSeq(pages.get(fromPageSeq) ?? Math.max(pages.size, 1))
}
